import type {
  TournamentControllerAddTeamData,
  TournamentControllerAddTeamResponse,
  TournamentControllerChangeTeamsOrderData,
  TournamentControllerChangeTeamsOrderResponse,
  TournamentControllerCreateData,
  TournamentControllerCreateResponse,
  TournamentControllerDeleteResponse,
  TournamentControllerDeleteTeamData,
  TournamentControllerDeleteTeamResponse,
  TournamentControllerFindAllData,
  TournamentControllerFindAllResponse,
  TournamentControllerFindOneData,
  TournamentControllerFindOneResponse,
  TournamentControllerGetTournamentParticipantsData,
  TournamentControllerGetTournamentParticipantsResponse,
  TournamentControllerGetTournamentTeamsData,
  TournamentControllerGetTournamentTeamsResponse,
  TournamentControllerSyncWithParentTournamentData,
  TournamentControllerSyncWithParentTournamentResponse,
  TournamentControllerUpdateData,
  TournamentControllerUpdatePhotosData,
  TournamentControllerUpdatePhotosResponse,
  TournamentControllerUpdateResponse,
  TournamentControllerUpdateResultsData,
  TournamentControllerUpdateResultsResponse,
  TournamentControllerUpdateTeamData,
  TournamentControllerUpdateTeamResponse,
} from '~/api';
import {
  tournamentControllerAddTeam,
  tournamentControllerChangeTeamsOrder,
  tournamentControllerCreate,
  tournamentControllerDelete,
  tournamentControllerDeleteTeam,
  tournamentControllerFindAll,
  tournamentControllerFindOne,
  tournamentControllerGetTournamentParticipants,
  tournamentControllerGetTournamentTeams,
  tournamentControllerSyncWithParentTournament,
  tournamentControllerUpdate,
  tournamentControllerUpdatePhotos,
  tournamentControllerUpdateResults,
  tournamentControllerUpdateTeam,
} from '~/api';
import { heyApiQuery } from '~/utils/api';

import api from '..';

export const tournaments = api.injectEndpoints({
  endpoints: builder => ({
    paginateTournaments: builder.query<TournamentControllerFindAllResponse, TournamentControllerFindAllData['query']>({
      queryFn: query => heyApiQuery(() => tournamentControllerFindAll({ query })),
      providesTags: (result, _error) =>
        result?.success
          ? [
              ...result.data.items.map(({ id }: { id: number }) => ({ type: 'TOURNAMENT', id }) as const),
              { type: 'TOURNAMENT', id: 'ALL' },
            ]
          : [{ type: 'TOURNAMENT', id: 'ALL' }],
    }),
    getTournament: builder.query<TournamentControllerFindOneResponse, TournamentControllerFindOneData['path']>({
      queryFn: path => heyApiQuery(() => tournamentControllerFindOne({ path })),
      providesTags: (result, _error, path) => (result?.success ? [{ type: 'TOURNAMENT', id: path.id }] : []),
    }),
    getTournamentParticipants: builder.query<
      TournamentControllerGetTournamentParticipantsResponse,
      TournamentControllerGetTournamentParticipantsData['path']
    >({
      queryFn: path => heyApiQuery(() => tournamentControllerGetTournamentParticipants({ path })),
      providesTags: (result, _error, path) =>
        result?.success ? [{ type: 'TOURNAMENT_PARTICIPANTS', id: path.id }] : [],
    }),
    getTournamentTeams: builder.query<
      TournamentControllerGetTournamentTeamsResponse,
      TournamentControllerGetTournamentTeamsData['path']
    >({
      queryFn: path => heyApiQuery(() => tournamentControllerGetTournamentTeams({ path })),
      providesTags: (result, _error) =>
        result?.success
          ? [
              ...result.data.map(({ id }: { id: number }) => ({ type: 'TOURNAMENT_TEAM', id }) as const),
              { type: 'TOURNAMENT_TEAM', id: 'ALL' },
            ]
          : [{ type: 'TOURNAMENT_TEAM', id: 'ALL' }],
    }),
    changeTournamentTeamsOrder: builder.mutation<
      TournamentControllerChangeTeamsOrderResponse,
      {
        id: TournamentControllerChangeTeamsOrderData['path']['tournamentId'];
        body: TournamentControllerChangeTeamsOrderData['body'];
      }
    >({
      queryFn: ({ id, body }) =>
        heyApiQuery(() => tournamentControllerChangeTeamsOrder({ path: { tournamentId: id }, body })),
      invalidatesTags: (result, _error) => (result?.success ? [{ type: 'TOURNAMENT_TEAM', id: 'ALL' }] : []),
    }),
    createTournament: builder.mutation<
      TournamentControllerCreateResponse,
      { params: TournamentControllerCreateData['body'] }
    >({
      queryFn: ({ params }) => heyApiQuery(() => tournamentControllerCreate({ body: params })),
      invalidatesTags: () => [{ type: 'TOURNAMENT', id: 'ALL' }],
    }),
    updateTournamentPhotos: builder.mutation<
      TournamentControllerUpdatePhotosResponse,
      { id: TournamentControllerUpdatePhotosData['path']['id']; params: TournamentControllerUpdatePhotosData['body'] }
    >({
      queryFn: ({ id, params }) => heyApiQuery(() => tournamentControllerUpdatePhotos({ path: { id }, body: params })),
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'TOURNAMENT', id },
              { type: 'TOURNAMENT', id: 'ALL' },
            ]
          : [],
    }),
    updateTournament: builder.mutation<
      TournamentControllerUpdateResponse,
      { id: TournamentControllerUpdateData['path']['id']; params: TournamentControllerUpdateData['body'] }
    >({
      queryFn: ({ id, params }) => heyApiQuery(() => tournamentControllerUpdate({ path: { id }, body: params })),
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'TOURNAMENT', id },
              { type: 'TOURNAMENT', id: 'ALL' },
            ]
          : [],
    }),
    syncWithParentTournament: builder.mutation<
      TournamentControllerSyncWithParentTournamentResponse,
      {
        id: TournamentControllerSyncWithParentTournamentData['path']['id'];
        params: TournamentControllerSyncWithParentTournamentData['body'];
      }
    >({
      queryFn: ({ id, params }) =>
        heyApiQuery(() => tournamentControllerSyncWithParentTournament({ path: { id }, body: params })),
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'TOURNAMENT', id },
              { type: 'TOURNAMENT', id: 'ALL' },
            ]
          : [],
    }),
    updateTournamentResults: builder.mutation<
      TournamentControllerUpdateResultsResponse,
      { id: TournamentControllerUpdateResultsData['path']['id']; params: TournamentControllerUpdateResultsData['body'] }
    >({
      queryFn: ({ id, params }) => heyApiQuery(() => tournamentControllerUpdateResults({ path: { id }, body: params })),
      invalidatesTags: (result, _error, { id }) =>
        result?.success
          ? [
              { type: 'TOURNAMENT', id },
              { type: 'TOURNAMENT', id: 'ALL' },
              { type: 'TOURNAMENT_TEAM', id: 'ALL' },
            ]
          : [],
    }),
    deleteTournament: builder.mutation<TournamentControllerDeleteResponse, number>({
      queryFn: id => heyApiQuery(() => tournamentControllerDelete({ path: { id } })),
      invalidatesTags: () => [{ type: 'TOURNAMENT', id: 'ALL' }],
    }),

    deleteTournamentTeam: builder.mutation<
      TournamentControllerDeleteTeamResponse,
      {
        tournamentId: TournamentControllerDeleteTeamData['path']['tournamentId'];
        tournamentTeamId: TournamentControllerDeleteTeamData['path']['tournamentTeamId'];
      }
    >({
      queryFn: ({ tournamentTeamId, tournamentId }) =>
        heyApiQuery(() => tournamentControllerDeleteTeam({ path: { tournamentId, tournamentTeamId } })),
      invalidatesTags: () => [{ type: 'TOURNAMENT_TEAM', id: 'ALL' }],
    }),
    addTournamentTeam: builder.mutation<
      TournamentControllerAddTeamResponse,
      {
        tournamentId: TournamentControllerAddTeamData['path']['tournamentId'];
        params: TournamentControllerAddTeamData['body'];
      }
    >({
      queryFn: ({ tournamentId, params }) =>
        heyApiQuery(() => tournamentControllerAddTeam({ path: { tournamentId }, body: params })),
      invalidatesTags: () => [{ type: 'TOURNAMENT_TEAM', id: 'ALL' }],
    }),
    updateTournamentTeam: builder.mutation<
      TournamentControllerUpdateTeamResponse,
      {
        tournamentId: TournamentControllerUpdateTeamData['path']['tournamentId'];
        tournamentTeamId: TournamentControllerUpdateTeamData['path']['tournamentTeamId'];
        params: TournamentControllerUpdateTeamData['body'];
      }
    >({
      queryFn: ({ tournamentId, tournamentTeamId, params }) =>
        heyApiQuery(() => tournamentControllerUpdateTeam({ path: { tournamentId, tournamentTeamId }, body: params })),
      invalidatesTags: () => [{ type: 'TOURNAMENT_TEAM', id: 'ALL' }],
    }),
  }),
  overrideExisting: false,
});
